import { useState } from 'react'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'
import { Link } from '@wartek-id/link'
import { Shimmer } from '@wartek-id/shimmer'
import { Button } from '@wartek-id/button-v1'
import type { BadgeProps } from '@wartek-id/badge'

import { useAPIGetCategories } from 'api/inspirations/useAPIInspirations'
import { Alert } from 'components/Alert'
import HeaderBadge from './HeaderBadge'

import type { FC } from 'react'

const MAX_COLUMNS = 3

type CategoryItem = {
  title: string
  slug: string
  displayRow: number
  displayRowOrder: number
}

type CategoriesObj = Record<number, Record<number, CategoryItem>>

type CategoryBlockVariant = 'revamp' | 'v2'

type VariantStyles = {
  wrapper: string
  dataContainer: string
  textColor: 'inverse' | undefined
  linkVariant: 'white' | undefined
  loadingContainer: string
  badgeVariant: BadgeProps['variant'] | undefined
  paddingBottom: string
}

const VARIANT_STYLES: Record<CategoryBlockVariant, VariantStyles> = {
  revamp: {
    wrapper: 'bg-[#0036A3]',
    dataContainer: 'from-[#0036A3] to-[#446FC3] bg-gradient-to-b pb-1',
    textColor: 'inverse',
    linkVariant: 'white',
    loadingContainer: 'from-[#0036A3] to-[#446FC3] bg-gradient-to-b',
    badgeVariant: undefined,
    paddingBottom: 'pb-2',
  },
  v2: {
    wrapper: '',
    dataContainer: 'border-b border-gray-20',
    textColor: undefined,
    linkVariant: undefined,
    loadingContainer: '',
    badgeVariant: 'informational',
    paddingBottom: 'pb-3',
  },
}

type CategoryBlockProps = {
  maxRows?: number
  variant?: CategoryBlockVariant
}

const CategoryBlock: FC<CategoryBlockProps> = ({
  maxRows = 3,
  variant = 'revamp',
}) => {
  const styles = VARIANT_STYLES[variant]
  const router = useRouter()
  const [categoriesObj, setCategoriesObj] = useState<CategoriesObj>({})

  const { data, isLoading, ...api } = useAPIGetCategories(null, {
    onSuccess: (res) => {
      // Filter to only diplay row 1-3
      const unordered = res.data.filter(
        (value) => !!value.title && value.displayRow <= maxRows
      )

      // Sort values by displayRow and displayRowOrder
      const ordered = [...unordered].sort(
        (a, b) =>
          a.displayRow - b.displayRow || a.displayRowOrder - b.displayRowOrder
      )

      // Form as an object
      const categories = ordered.reduce<CategoriesObj>((obj, category) => {
        const row = obj[category.displayRow] || {}
        return {
          ...obj,
          [category.displayRow]: {
            ...row,
            [category.displayRowOrder]: category as CategoryItem,
          },
        }
      }, {})

      setCategoriesObj(categories)
    },
  })

  const showTime = ({ rowIndex, columnLength, columnIndex }) => {
    if (
      rowIndex === Object.keys(categoriesObj).length - 1 && // Apply to the last row
      rowIndex >= 1 && // Apply to the second row or more (hide if only 1 row available)
      columnIndex === columnLength - 1 &&
      columnIndex >= MAX_COLUMNS - 1
    )
      return true

    return false
  }

  return (
    <>
      <div className={clsx('flex flex-col', styles.wrapper)}>
        {/* Category */}
        {!isLoading && data?.data?.length >= 1 && (
          <div className="flex items-center justify-between px-4 pt-2 pb-3 space-x-3 justify-items-center">
            <Text variant="body-sm" color={styles.textColor}>
              Pilih kategori
            </Text>
            <NextLink href="/ide-praktik/kategori" passHref>
              <Link
                tabIndex={0}
                underline
                variant={styles.linkVariant}
                className={clsx(
                  'text-xs font-semibold',
                  variant === 'v2' && '!-mr-1'
                )}
                data-testid="category-show-all-link"
              >
                Lihat semua
              </Link>
            </NextLink>
          </div>
        )}

        {api.isError && (
          <div className="mx-4">
            <Alert color="orange-10" icon="" className="my-1 !p-[10px]">
              Gagal menampilkan kategori. Harap coba lagi, atau langsung lihat
              semua kategori.
            </Alert>
            <div className="my-3">
              <Button
                color="white"
                size="sm"
                className="!bg-transparent !text-inverse hover:!bg-gray-5 hover:!text-gray-90"
                data-testid="category-show-all-button"
                onClick={() => {
                  router.push(`/ide-praktik/kategori`)
                }}
              >
                Lihat semua kategori
              </Button>
            </div>
          </div>
        )}

        {/* Category loading */}
        {isLoading && (
          <>
            <div className="flex items-center justify-between px-4 pt-2 pb-3 space-x-3 justify-items-center">
              {Array.from({ length: 2 })?.map((_, index) => (
                <Shimmer
                  height={13 - index}
                  noOfLines={1}
                  key={`loading-shimmer-${index}`}
                  width="25%"
                />
              ))}
            </div>
            <div
              className={clsx(
                'px-4 pb-1 overflow-x-hidden',
                styles.loadingContainer
              )}
              style={{ margin: '-1px 0 1px' }}
            >
              <div
                className="flex flex-row flex-wrap items-center content-center justify-start"
                style={{ width: '125vw' }}
              >
                {Array.from({ length: 6 })?.map((_, index) => (
                  <Shimmer
                    height={26}
                    noOfLines={1}
                    key={`loading-shimmer-item-${index}`}
                    width={`${
                      index % 3 === 0 ? '35' : index % 3 === 1 ? 40 : '45'
                    }%`}
                    className="mr-2 mb-2 !rounded-2xl"
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {!isLoading && (
        <>
          {/* If has data */}
          {data?.data?.length >= 1 && (
            <div
              className={clsx('px-4 overflow-x-auto', styles.dataContainer)}
              style={{ margin: '-1px 0 1px' }}
            >
              <div
                className={clsx(
                  'flex flex-col flex-wrap space-y-2',
                  styles.paddingBottom
                )}
              >
                {Object.keys(categoriesObj).map((rowKey, rowIndex) => {
                  const row = categoriesObj[rowKey]
                  const rowKeys = Object.keys(row)
                  return (
                    <div
                      className="flex space-x-2"
                      data-testid={`category-row-${rowIndex + 1}`}
                      key={`category-${rowIndex}`}
                    >
                      {rowKeys.map((columnKey, columnIndex) => {
                        const cat = row[columnKey]
                        const testId = `category-row-${rowIndex + 1}-col-${
                          columnIndex + 1
                        }`

                        return (
                          <div
                            className="flex-none"
                            key={`category-${rowIndex}-${columnIndex}`}
                          >
                            <div
                              className="flex space-x-2"
                              data-testid={testId}
                            >
                              {/* Basic category clickable badge */}
                              <HeaderBadge
                                label={cat?.title}
                                href={`/ide-praktik/kategori/${cat?.slug}`}
                                className="!bg-blue-10"
                                variant={styles.badgeVariant}
                              />

                              {/* Show All clickable badge */}
                              {showTime({
                                rowIndex,
                                columnLength: rowKeys.length,
                                columnIndex,
                              }) && (
                                <div
                                  className="!mr-2"
                                  data-testid="category-show-all-badge"
                                >
                                  <HeaderBadge
                                    label="Lihat semua"
                                    href="/ide-praktik/kategori"
                                    className="!bg-surface"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default CategoryBlock
